import { useCallback, useEffect, useState } from 'react'
import { Header, type View } from './components/Header'
import { Board } from './components/Board'
import { Ticker } from './components/Ticker'
import { Heatmap } from './components/Heatmap'
import { Method } from './components/Method'
import { K, mono } from './lib/theme'
import { getBoard, getConfig, type Board as BoardData, type Config } from './lib/api'

export function App() {
  const [view, setView] = useState<View>('board')
  const [sym, setSym] = useState('SPY')
  const [board, setBoard] = useState<BoardData | null>(null)
  const [cfg, setCfg] = useState<Config | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [now, setNow] = useState(new Date())

  useEffect(() => {
    const load = () => getBoard().then((b) => { setBoard(b); setErr(null) }).catch((e) => setErr(e.message))
    load()
    const t = setInterval(load, 60_000)
    return () => clearInterval(t)
  }, [])

  useEffect(() => { getConfig().then(setCfg).catch(() => {}) }, [])
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t) }, [])

  const openTicker = useCallback((s: string) => { setSym(s); setView('ticker') }, [])

  return (
    <div style={{
      minHeight: '100vh', backgroundColor: K.bg, color: K.txt,
      backgroundImage: 'linear-gradient(rgba(154,163,199,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(154,163,199,.05) 1px,transparent 1px)',
      backgroundSize: '48px 48px', paddingBottom: 80,
    }}>
      <Header view={view} setView={setView} board={board} now={now} />
      {err && (
        <div style={{ margin: '12px clamp(16px,3vw,28px) 0', border: `1px solid ${K.am}`, padding: '10px 14px', fontSize: 13, color: K.txt, maxWidth: 1240, marginLeft: 'auto', marginRight: 'auto' }}>
          <span style={{ fontFamily: mono, color: K.am }}>FEED </span>
          Could not reach the Offbell API ({err}). Prices below may be out of date; nothing is being estimated.
        </div>
      )}
      {view === 'board' && <Board board={board} cfg={cfg} now={now} openTicker={openTicker} />}
      {view === 'ticker' && <Ticker sym={sym} board={board} setSym={setSym} goHeat={() => setView('heatmap')} />}
      {view === 'heatmap' && <Heatmap sym={sym} board={board} setSym={setSym} />}
      {view === 'method' && <Method />}

      <footer style={{ maxWidth: 1240, margin: '48px auto 0', padding: '20px clamp(16px,3vw,28px)', borderTop: `1px solid ${K.line}`, display: 'flex', flexWrap: 'wrap', gap: '10px 24px', alignItems: 'center', fontSize: 12.5, color: K.dim }}>
        <span style={{ fontFamily: mono, color: K.mut }}>offbell.app</span>
        {cfg?.xUrl && <a href={cfg.xUrl} target="_blank" rel="noreferrer" style={{ color: K.cy, display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
          Follow on X
        </a>}
        <button onClick={() => setView('method')} style={{ background: 'none', border: 0, padding: 0, color: K.cy, fontSize: 'inherit', fontFamily: 'inherit', cursor: 'pointer' }}>Methodology</button>
        <span style={{ marginLeft: 'auto', maxWidth: '60ch', lineHeight: 1.5 }}>
          Stock tokens are on-chain tokens tracking an equity. Holding one is not ownership of the underlying share.
          Measurement only — nothing here is a signal or a forecast.
        </span>
      </footer>
    </div>
  )
}
